import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Building2, Car, Home, Package, Plus, Trees, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import type { Property } from '@app/shared';
import { useProperties } from '@/features/properties/queries';
import { PROPERTY_KIND_LABELS, PROPERTY_USAGE_LABELS } from '@/features/properties/labels';
import { useCategories } from '@/features/reference/queries';
import { useTransactions } from '@/features/ledger/queries';
import { formatCents } from '@/lib/format';
import { PERIOD_OPTIONS, periodRange, type PeriodOption } from '@/lib/period';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const KIND_COVER: Record<Property['kind'], { icon: LucideIcon; className: string }> = {
  apartment: { icon: Building2, className: 'bg-accent text-accent-foreground' },
  house: { icon: Home, className: 'bg-accent text-accent-foreground' },
  parking: { icon: Car, className: 'bg-muted text-muted-foreground' },
  land: { icon: Trees, className: 'bg-muted text-muted-foreground' },
  other: { icon: Package, className: 'bg-muted text-muted-foreground' },
};

const PROPERTY_COVER_IMAGES: Record<string, string> = {
  'Alcantarilha - Casa J': '/property-covers/alcantarilha-casa-j.png',
  'Casa DiLauNa': '/property-covers/casa-dilauna.jpg',
  'Lobéllia': '/property-covers/lobellia.jpg',
};

export function PropertiesPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [period, setPeriod] = useState<PeriodOption>('1');
  const properties = useProperties(includeArchived);
  const categories = useCategories();
  const { dateFrom, dateTo } = periodRange(period);
  const transactions = useTransactions({ status: 'validated', dateFrom, dateTo });

  const totalsByProperty = useMemo(() => {
    const directionByCategory = new Map(categories.data?.map((category) => [category.id, category.direction]));
    const totals = new Map<number, { incomeCents: number; expenseCents: number }>();
    for (const tx of transactions.data ?? []) {
      if (tx.propertyId === null || tx.categoryId === null) continue;
      const entry = totals.get(tx.propertyId) ?? { incomeCents: 0, expenseCents: 0 };
      if (directionByCategory.get(tx.categoryId) === 'income') entry.incomeCents += tx.amountCents;
      else entry.expenseCents += Math.abs(tx.amountCents);
      totals.set(tx.propertyId, entry);
    }
    return totals;
  }, [transactions.data, categories.data]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Biens</h1>
        <Button asChild>
          <Link to="/biens/nouveau">
            <Plus /> Nouveau bien
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
            className="size-4 rounded border-input"
          />
          Afficher les biens archivés
        </label>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Revenus / dépenses sur</span>
          <Select value={period} onValueChange={(value) => setPeriod(value as PeriodOption)}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {properties.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {properties.isError && <p className="text-destructive">Impossible de charger les biens.</p>}

      {properties.data?.length === 0 && (
        <p className="text-muted-foreground">Aucun bien pour l'instant.</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {properties.data?.map((property) => {
          const cover = KIND_COVER[property.kind];
          const CoverIcon = cover.icon;
          const coverImage = PROPERTY_COVER_IMAGES[property.name];
          const location = [property.city, property.country].filter(Boolean).join(', ');
          const totals = totalsByProperty.get(property.id);
          return (
            <Link key={property.id} to="/biens/$propertyId" params={{ propertyId: String(property.id) }}>
              <Card className="h-full overflow-hidden py-0 transition-colors hover:border-primary/50">
                <div className={`relative flex h-36 items-center justify-center ${cover.className}`}>
                  {coverImage ? (
                    <img src={coverImage} alt="" className="size-full object-cover" />
                  ) : (
                    <CoverIcon className="size-10 opacity-40" />
                  )}
                  {location && (
                    <span className="absolute left-3 top-3 rounded-md bg-card/90 px-2.5 py-1 text-xs font-semibold text-primary">
                      {location}
                    </span>
                  )}
                </div>
                <CardHeader className="pt-5">
                  <CardTitle className="flex items-center justify-between gap-2 font-serif text-lg">
                    {property.name}
                    {property.archivedAt && <Badge variant="secondary">Archivé</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 pb-5 text-sm text-muted-foreground">
                  <p>
                    {PROPERTY_KIND_LABELS[property.kind]} · {PROPERTY_USAGE_LABELS[property.usage]}
                  </p>
                  <div className="flex items-center justify-between text-sm font-semibold tabular-nums">
                    <span className="flex items-center gap-1 text-emerald-700">
                      <TrendingUp className="size-4" />
                      {formatCents(totals?.incomeCents ?? 0, property.currency)}
                    </span>
                    <span className="flex items-center gap-1 text-destructive">
                      <TrendingDown className="size-4" />
                      {formatCents(totals?.expenseCents ?? 0, property.currency)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
